import Cookies from "js-cookie";
import {
  ChevronDown,
  ClipboardList,
  FlaskConical,
  KeyRound,
  LogIn,
  Settings2,
  User,
} from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { resetAnalyticsUser, track } from "../analytics";
import { useStateLS, useUser } from "../utils";
import { AppearanceDialog } from "./AppearanceDialog";
import { useOpenOnboardingSurvey } from "./onboarding/context";

export function UserDropdown(): React.ReactElement {
  const { data: user } = useUser();
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const openOnboardingSurvey = useOpenOnboardingSurvey();
  const [useExperimental, setUseExperimental] = useStateLS(
    "useExperimental",
    false,
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full max-w-44 gap-1"
            // The username is conditional, so without this the trigger has no
            // accessible name at all while the user is loading. Keeping the name
            // inside the label satisfies "Label in Name" for speech input.
            aria-label={
              user?.name ? `Account menu for ${user.name}` : "Account menu"
            }
          >
            <User className="w-5 h-5 flex-none" aria-hidden="true" />
            {/* Hidden, not dropped, below sm: the name is already in the
                aria-label above, so the control keeps its full name either way. */}
            {user?.name && (
              <span className="hidden truncate text-sm sm:inline">
                {user.name}
              </span>
            )}
            <ChevronDown className="w-4 h-4 flex-none" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 z-40">
          {/* Admin panel and File browser used to live here; they are destinations,
              not account actions, so they moved to the sidebar's admin section.
              What is left is only what is about this account. */}
          {/* onSelect, not onClick: it is the handler Radix fires for keyboard
            activation as well as pointer. */}
          <DropdownMenuItem onSelect={() => setAppearanceOpen(true)}>
            <Settings2 className="w-4 h-4 mr-2" aria-hidden="true" />
            Appearance &amp; accessibility
          </DropdownMenuItem>
          {/* The dialog itself lives in OnboardingSurveyProvider, not here: see the note
              on AppearanceDialog below for what happens when a dialog is nested in a menu. */}
          <DropdownMenuItem onSelect={() => openOnboardingSurvey()}>
            <ClipboardList className="w-4 h-4 mr-2" aria-hidden="true" />
            About you
          </DropdownMenuItem>
          {user?.canUseExperimentalFeatures && (
            <DropdownMenuItem
              onClick={() => setUseExperimental(!useExperimental)}
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              Experimental: {useExperimental ? "On" : "Off"}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <a href="/password-change/">
              <KeyRound className="w-4 h-4 mr-2" />
              Change password
            </a>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              fetch("/logout/", {
                method: "POST",
                headers: { "x-csrftoken": Cookies.get("csrftoken") ?? "" },
              }).then(() => {
                track("user_signed_out", {});
                resetAnalyticsUser();
                window.location.href = "/";
              });
            }}
          >
            <LogIn className="w-4 h-4 mr-2" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Outside the DropdownMenu entirely. Nested inside it, the menu and the
          dialog are two focus-managing layers both trying to restore focus as
          the menu closes and the dialog opens, and focus ends up on <body>. */}
      <AppearanceDialog
        open={appearanceOpen}
        onOpenChange={setAppearanceOpen}
      />
    </>
  );
}
